<template>
  <div class="funcoes-pipeline">
    <div v-if="!passos.length" class="funcoes-vazio">
      Nenhuma função aplicada — o valor é enviado como está.
    </div>

    <div v-if="passos.length > 1" class="info-arrasto">
      A ordem das funções define o resultado. Arraste pelo <fa-icon icon="grip-vertical" /> para reordenar.
    </div>

    <draggable
      :list="passos"
      handle=".drag-handle"
      animation="200"
      ghost-class="funcao-linha--dragging"
      @end="onReordenar"
    >
      <div v-for="(passo, i) in passos" :key="i" class="funcao-linha">
        <span class="drag-handle" v-if="passos.length > 1" title="Arraste para reordenar" aria-label="Arraste para reordenar">
          <fa-icon icon="grip-vertical" />
        </span>
        <span class="ordem">#{{ i + 1 }}</span>
        <div class="funcao-corpo">
          <select
            class="form-control form-control-sm"
            :value="passo.funcao || ''"
            @change="alterarFuncao(i, $event.target.value)"
          >
            <option value="">Selecione uma função</option>
            <optgroup v-for="grupo in grupos" :key="grupo.chave" :label="grupo.titulo">
              <option v-for="f in grupo.funcoes" :key="f.chave" :value="f.chave">{{ f.label }}</option>
            </optgroup>
          </select>
          <div v-if="argsDe(passo.funcao).length" class="funcao-args">
            <div v-for="arg in argsDe(passo.funcao)" :key="arg.nome" class="funcao-arg">
              <label class="field-label">{{ arg.label }}<span v-if="arg.obrigatorio" class="arg-obrigatorio">*</span></label>
              <input
                class="form-control form-control-sm"
                :type="arg.tipo === 'int' || arg.tipo === 'number' ? 'number' : 'text'"
                :value="valorArg(passo, arg.nome)"
                @input="alterarArg(i, arg, $event.target.value)"
              />
            </div>
          </div>
        </div>
        <button
          type="button"
          class="btn-mini btn-mini--danger"
          @click="remover(i)"
          title="Remover função"
          aria-label="Remover função"
        >
          <fa-icon icon="trash-alt" />
        </button>
      </div>
    </draggable>

    <button type="button" class="btn-add-inline" @click="adicionar">
      <fa-icon icon="plus" /> Adicionar função
    </button>

    <div v-if="passosConfigurados.length" class="funcoes-preview">
      <label class="field-label">Pré-visualização</label>
      <div class="preview-linha">
        <input
          class="form-control form-control-sm preview-input"
          type="text"
          v-model="exemplo"
          placeholder="Ex.: 12345-004-0101 (ou um JSON p/ funções de lista)"
        />
        <span class="preview-seta">→</span>
        <span v-if="preview.erro" class="preview-erro" :title="preview.erro">
          <fa-icon icon="exclamation-triangle" /> {{ preview.erro }}
        </span>
        <span v-else class="preview-resultado" :class="classeValor(preview.resultado)">
          {{ exibirValor(preview.resultado) }}
        </span>
      </div>
      <div v-if="!preview.erro && passosConfigurados.length > 1" class="preview-passos">
        <span class="preview-passo">{{ exibirValor(valorExemplo) }}</span>
        <template v-for="(estagio, i) in preview.estagios">
          <span :key="'seta' + i" class="preview-seta-mini">→</span>
          <span :key="'val' + i" class="preview-passo" :title="estagio.funcao">{{ exibirValor(estagio.valor) }}</span>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import { simularFuncoes } from "./aplicarFuncoes";

const TITULOS_GRUPO = {
  texto: "Texto",
  numero: "Número",
  lista: "Lista / Agregação",
};

export default {
  name: "FuncoesPipeline",
  components: { draggable },
  inject: {
    catalogoFuncoes: { default: null },
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      exemplo: "",
    };
  },
  computed: {
    passosConfigurados() {
      return this.passos.filter((p) => p && p.funcao);
    },
    valorExemplo() {
      // Para funções de lista (ITEM/FIRST/SUM…) o exemplo pode ser um JSON
      // (array/objeto). Texto comum é mantido como string.
      const texto = (this.exemplo || "").trim();
      if (texto.startsWith("[") || texto.startsWith("{")) {
        try {
          return JSON.parse(texto);
        } catch (e) {
          return this.exemplo;
        }
      }
      return this.exemplo;
    },
    preview() {
      return simularFuncoes(this.valorExemplo, this.passosConfigurados);
    },
    catalogo() {
      return this.catalogoFuncoes && Array.isArray(this.catalogoFuncoes.value)
        ? this.catalogoFuncoes.value
        : [];
    },
    passos() {
      return Array.isArray(this.value) ? this.value : [];
    },
    grupos() {
      const ordem = ["texto", "numero", "lista"];
      const porGrupo = {};
      this.catalogo.forEach((f) => {
        (porGrupo[f.grupo] = porGrupo[f.grupo] || []).push(f);
      });
      return ordem
        .filter((chave) => porGrupo[chave])
        .map((chave) => ({
          chave,
          titulo: TITULOS_GRUPO[chave] || chave,
          funcoes: porGrupo[chave],
        }));
    },
  },
  methods: {
    metaDe(chave) {
      return this.catalogo.find((f) => f.chave === chave) || null;
    },
    argsDe(chave) {
      const meta = this.metaDe(chave);
      return meta && Array.isArray(meta.args) ? meta.args : [];
    },
    valorArg(passo, nome) {
      return passo.args && passo.args[nome] != null ? passo.args[nome] : "";
    },
    emitir(passos) {
      this.$emit("input", passos);
    },
    adicionar() {
      this.emitir([...this.passos, { funcao: "", args: {} }]);
    },
    remover(i) {
      const copia = this.passos.slice();
      copia.splice(i, 1);
      this.emitir(copia);
    },
    onReordenar() {
      // `draggable` reordena a lista in-place; emite uma cópia nova para o pai.
      this.emitir(this.passos.slice());
    },
    alterarFuncao(i, funcao) {
      const copia = this.passos.slice();
      // Troca de função zera os argumentos (schemas diferentes).
      copia[i] = { funcao, args: {} };
      this.emitir(copia);
    },
    alterarArg(i, arg, valor) {
      const copia = this.passos.slice();
      const args = { ...(copia[i].args || {}) };
      if (valor === "") {
        delete args[arg.nome];
      } else {
        args[arg.nome] = arg.tipo === "int" || arg.tipo === "number" ? Number(valor) : valor;
      }
      copia[i] = { ...copia[i], args };
      this.emitir(copia);
    },
    exibirValor(valor) {
      if (valor === null || valor === undefined) return "(vazio)";
      if (typeof valor === "string") return `'${valor}'`;
      if (typeof valor === "boolean") return valor ? "true" : "false";
      if (typeof valor === "object") return JSON.stringify(valor);
      return String(valor);
    },
    classeValor(valor) {
      if (valor === null || valor === undefined) return "preview-resultado--vazio";
      return "";
    },
  },
};
</script>

<style scoped>
.funcoes-pipeline {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.funcoes-vazio {
  font-size: 12px;
  color: #98a2b3;
  font-style: italic;
}
.info-arrasto {
  font-size: 11px;
  color: #98a2b3;
  margin-bottom: 2px;
}
.funcao-linha {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 6px;
}
.drag-handle {
  cursor: grab;
  color: #98a2b3;
  padding: 8px 2px 0;
  font-size: 12px;
  flex-shrink: 0;
}
.drag-handle:active {
  cursor: grabbing;
}
.funcao-linha--dragging {
  opacity: 0.4;
}
.ordem {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  background: #eceaf3;
  padding: 6px 7px;
  border-radius: 4px;
  flex-shrink: 0;
}
.funcao-corpo {
  /* Largura fixa (≈ tamanho do campo), estável ao adicionar etapas — não
     estica a linha toda. */
  flex: 0 1 340px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.funcao-args {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 8px;
}
.funcao-arg .field-label {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 3px;
  display: block;
}
.arg-obrigatorio {
  color: #d92d20;
  margin-left: 2px;
}
.btn-mini {
  background: none;
  border: none;
  cursor: pointer;
  color: #98a2b3;
  padding: 8px 6px 0;
  border-radius: 4px;
  flex-shrink: 0;
}
.btn-mini--danger:hover {
  color: #d92d20;
  background: #fef3f2;
}
.btn-add-inline {
  align-self: flex-start;
  background: none;
  border: none;
  color: #3a3e81;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 0;
  cursor: pointer;
}
.btn-add-inline:hover {
  text-decoration: underline;
}
.funcoes-preview {
  margin-top: 4px;
  padding: 8px 10px;
  border: 1px dashed #d0d5dd;
  border-radius: 8px;
  background: #fcfcfe;
  max-width: 560px;
}
.funcoes-preview .field-label {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 6px;
  display: block;
}
.preview-linha {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.preview-input {
  flex: 0 1 240px;
  min-width: 0;
  height: 30px;
  font-family: 'Menlo', 'Consolas', monospace;
  font-size: 12px;
}
.preview-seta {
  color: #98a2b3;
  font-weight: 600;
  flex-shrink: 0;
}
.preview-resultado {
  font-family: 'Menlo', 'Consolas', monospace;
  font-weight: 600;
  color: #15803d;
  background: #dcfce7;
  padding: 4px 10px;
  border-radius: 6px;
  word-break: break-all;
}
.preview-resultado--vazio {
  color: #98a2b3;
  background: #f2f4f7;
  font-style: italic;
}
.preview-erro {
  font-size: 12px;
  color: #b42318;
  background: #fef3f2;
  padding: 4px 10px;
  border-radius: 6px;
}
.preview-passos {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 8px;
  font-family: 'Menlo', 'Consolas', monospace;
  font-size: 12px;
  color: #475467;
}
.preview-passo {
  background: #fff;
  border: 1px solid #e4e7eb;
  border-radius: 4px;
  padding: 2px 7px;
}
.preview-seta-mini {
  color: #98a2b3;
}
</style>
